/**
 * @dosia/shared — Unified type definitions
 *
 * Single source of truth for all types shared across:
 * - @dosia/core (business logic)
 * - @dosia/ui (React components)
 * - apps/web (Express server + Vite client)
 * - apps/desktop (Electron main/preload/renderer)
 */
/** USD 折算数字一律带这个标签：估算，不是账单复刻，也不是实时价。 */
export const CACHE_MISS_ESTIMATE_LABEL = "估算·按 as-of 价";
export const CACHE_MISS_CURRENCY = "USD";
/**
 * 成功结果文本尾部机器可读段的哨兵前缀。格式：
 *   `[Codex 委派完成]\n<finalText>\n<!--dosia:delegate_codex:<JSON of
 *   DelegateCodexStructured>-->\n\n<R1 额度尾注>`
 * 解析端定位 `<!--<marker>` … `-->`（取**最后一次出现**，防 finalText 内恰好含类似字样时
 * 被截断/劫持——真实 trailer 恒是文本里最后追加的一段），中间 JSON.parse 出 metadata（guard
 * 每字段）；finalText 从「header 之后 → trailer 起点之前」的正文原样切出，不进 JSON。渲染端
 * 剥离 trailer 段后不再展示（用户只看结构化卡），R1 尾注留在 trailer 之后、纯文本兜底面仍可见。
 */
export const DELEGATE_CODEX_RESULT_MARKER = "dosia:delegate_codex:";
/**
 * delegate_codex 开关的**唯一** normalizer（红线 #9）。把 boolean 简写 / 全字段对象 /
 * 脏值·畸形输入统一收敛为 {@link NormalizedDelegateCodexConfig}：
 *  - 默认：`{ enabled: false, maxSandbox: "workspace-write", reviewGate: false }`（关）。
 *  - `true` → `{ enabled: true, maxSandbox: "workspace-write", reviewGate: false }`。
 *  - 对象且 `enabled === true` → 逐字段独立取值（maxSandbox / reviewGate 各自降级到默认）。
 *  - `false` / `null` / undefined / 非对象 / `enabled` 非 true → 全默认即关（enabled=false 时
 *    maxSandbox/reviewGate 无语义，一律落默认避免残留脏态）。
 * 永不 throw（对不可信输入面友好降级，红线 #9 反序列化防御）。
 */
export function normalizeDelegateCodexConfig(v) {
    const DEFAULT = {
        enabled: false,
        maxSandbox: "workspace-write",
        reviewGate: false,
        channelMode: "personal",
    };
    if (v === true)
        return { ...DEFAULT, enabled: true };
    if (typeof v !== "object" || v === null)
        return { ...DEFAULT };
    const o = v;
    if (o.enabled !== true)
        return { ...DEFAULT };
    return {
        enabled: true,
        maxSandbox: o.maxSandbox === "read-only" ? "read-only" : "workspace-write",
        reviewGate: o.reviewGate === true,
        channelMode: o.channelMode === "enterprise" || o.channelMode === "personal-first"
            ? o.channelMode
            : "personal",
        // defaultModel 仅在提供非空字符串时携带（否则消费端回落到「可用清单首个」）。
        ...(typeof o.defaultModel === "string" && o.defaultModel.trim()
            ? { defaultModel: o.defaultModel.trim() }
            : {}),
    };
}
/**
 * F3 委派通道决策的**唯一纯函数**（红线 #9）——把 `channelMode × 有无个人登录态` 收敛为
 * 实际要走的通道（`personal` / `enterprise`），供 desktop resolver 与测试共用一个真相源：
 *  - `enterprise` → 恒 `enterprise`（免登录，忽略个人态）。
 *  - `personal`   → 恒 `personal`（v1 行为）。
 *  - `personal-first` → 有个人 auth.json 走 `personal`，否则回落 `enterprise`（平滑迁移）。
 * 纯逻辑、无副作用、永不 throw。企业通道凭证解析失败的降级（E13）由调用侧处理，不在此函数。
 */
export function decideCodexChannel(channelMode, hasPersonalAuth) {
    if (channelMode === "enterprise")
        return "enterprise";
    if (channelMode === "personal")
        return "personal";
    return hasPersonalAuth ? "personal" : "enterprise"; // personal-first
}
/**
 * delegate_codex 开关的**形状粗校验**（反序列化边界防注入，红线 #9）——只判「是不是一个
 * 可被 normalizer 处理的合法形态」，**不做语义归一化**（那是消费端 normalizer 的活，禁两处
 * 各写一套规则）。接受：字面 boolean，或非数组的普通对象。拒绝：字符串 / 数字 / 数组 / null。
 * event-client 用它守 wire payload：过则原样透传（语义留给 normalizer），否则落 undefined。
 */
export function isDelegateCodexConfigShape(v) {
    if (typeof v === "boolean")
        return true;
    return typeof v === "object" && v !== null && !Array.isArray(v);
}
/** Runtime vocabulary mirroring the `ThinkingLevel` union — single source (红线#9)
 *  for level-filtering both ingestion points that decode the gateway M1 structured
 *  `thinking_param` descriptor (core/manifest-parser.ts's public-manifest ingestion
 *  AND bytespike-bundle-wire.ts's bundle-wire ingestion).
 *
 *  **ALSO the single source of truth for canonical presentation ORDER**
 *  (`minimal → low → medium → high → xhigh → max`, ascending effort). Catalog
 *  snapshots may declare `thinkingParam.levels` in any order (e.g. legacy
 *  `[…,"max","xhigh"]` with xhigh mis-ranked after max); every effort-level
 *  *display* point sorts through `sortByThinkingLevel` so the ladder renders
 *  in this order regardless of declaration order. The ingestion consumers only
 *  use it as `new Set(THINKING_LEVELS)` for membership — order-independent. */
export const THINKING_LEVELS = ["minimal", "low", "medium", "high", "xhigh", "max"];
/** Canonical presentation sort for effort/thinking levels (红线#9 single source):
 *  order by `THINKING_LEVELS` index ascending; any unknown value sorts to the end
 *  (stable among unknowns). Pure, does not mutate the input. */
export function sortByThinkingLevel(levels) {
    const rank = (l) => {
        const i = THINKING_LEVELS.indexOf(l);
        return i === -1 ? THINKING_LEVELS.length : i;
    };
    return [...levels].sort((a, b) => rank(a) - rank(b));
}
/**
 * Narrow a display ThinkingLevel to the SDK-native EffortLevel before it crosses
 * the SDK boundary. Claude's effort ladder now mirrors SDK 0.3.212:
 * low|medium|high|xhigh|max — all pass through. Only `minimal` (a Gemini display
 * level with no SDK effort equivalent) maps to undefined so it is never sent.
 */
export function toSdkEffort(level) {
    if (level === "low" || level === "medium" || level === "high" || level === "xhigh" || level === "max")
        return level;
    return undefined;
}
/**
 * Normalize an object ThinkingConfig so an adaptive config without an explicit
 * `display` gets `display: "summarized"` (the SDK default flipped to "omitted",
 * which blanks the thinking stream). Single normalization point — the
 * agent-session thinking sink passes any caller-supplied config through here so
 * no adaptive config leaks to the SDK without a display. Non-adaptive configs
 * (enabled/disabled) pass through unchanged.
 */
export function normalizeThinkingDisplay(config) {
    if (config.type === "adaptive" && config.display === undefined) {
        return { type: "adaptive", display: "summarized" };
    }
    return config;
}
/** Validate a publish target: {kind:"org"} | {kind:"roles",roleCodes:non-empty string[]}.
 *  Single source — reused by desktop IPC, the web forward route, and admin-portal. */
export function isMcpPublishTarget(t) {
    if (!t || typeof t !== "object")
        return false;
    const kind = t.kind;
    if (kind === "org")
        return true;
    if (kind === "roles") {
        const codes = t.roleCodes;
        return Array.isArray(codes) && codes.length > 0 && codes.every((c) => typeof c === "string" && !!c);
    }
    return false;
}
/** stdio 连接器分发防呆（OPT-6 B 方案）：本地**绝对路径命令**无法分发给他人——它指向
 *  提交者本机的安装目录 / 二进制，员工机上不存在该路径 → spawn 异步 ENOENT，连接器工具
 *  静默消失（probe REGISTRY-installed-bin 用例实证）。提交路由与审批路由**共用本单一真相源**
 *  （红线 #9），任一端命中即拒。放行：`npx -y pkg`（包身份在 args，员工机首启自动拉）、
 *  裸命令名（走员工机 PATH 解析，如 `node`/`python`）、远程 http/sse（无本机依赖）。
 *  跨平台：分发目标机平台未知，POSIX 绝对路径 / Windows 盘符 / UNC / `~` 家目录全部视为不可分发。 */
export function isDistributableMcpCommand(entry) {
    if (entry.type !== "stdio")
        return true; // http/sse remote — no machine-local binary
    const cmd = (entry.command ?? "").trim();
    if (!cmd)
        return true; // emptiness is caught by the separate submittable-shape guard, not here
    // Absolute/home-relative → submitter-local, non-distributable:
    //   /usr/local/bin/x  | C:\tools\x  | C:/tools/x  | \\host\share\x  | ~/x
    return !/^([a-zA-Z]:[\\/]|\/|\\\\|~)/.test(cmd);
}
/** User-facing rejection copy for a non-distributable (absolute-path) stdio connector.
 *  Single source so the submit route, approve route, and any UI share identical wording. */
export const NON_DISTRIBUTABLE_MCP_MESSAGE = "本地命令型无法分发给他人，请改用 npx 或远程接口";
/** Merge role-provisioned (admin-portal `role.dosia.mcpServers`) MCP entries into a
 *  workspace's `mcpServers`: preserve the user's own (non-managed) entries, drop stale
 *  managed ones, and re-add the role's current set tagged `managed:true` (role entries
 *  win on name collision). Single source for the enterprise-login reconcile
 *  (desktop workspace.ts) and any push-driven reconcile — mirrors the probe-verified
 *  submit→approve→distribute merge (connector-distribution-probe loginReconcile). */
export function reconcileManagedMcpServers(currentWorkspaceMcp, roleMcp) {
    const userMcp = Object.fromEntries(Object.entries(currentWorkspaceMcp ?? {}).filter(([, e]) => !e.managed));
    const managed = Object.fromEntries(Object.entries(roleMcp ?? {}).map(([name, e]) => [
        name,
        { ...e, name, managed: true },
    ]));
    return { ...userMcp, ...managed };
}
/**
 * Codex 编码助手场景（F7）的规范 capabilityRef 串——唯一常量（红线 #9），入口卡启动 /
 * 会话谓词判定共用。`codex` scheme 无子目标语义，parseCapabilityRef 对任意 `codex:<x>`
 * 都归一为 `{ kind: "codex" }`，但**写入**恒用本常量（buildCapabilityRef 产出）。 */
export const CODEX_CAPABILITY_REF = "codex:coding";
/**
 * CapabilityRef → 规范化 URI 串的**唯一构建器**（红线 #9：序列化产物单一真相源，供分享
 * 链接 / 任务绑定跨 producer↔consumer 复用，禁多处手搓字符串）。parseCapabilityRef 是其
 * 反向。
 */
export function buildCapabilityRef(ref) {
    switch (ref.kind) {
        case "skill":
            return `skill:${ref.skill}`;
        case "plugin":
            return `plugin:${ref.plugin}`;
        case "cluster":
            return `cluster:${ref.cluster}`;
        case "mcp":
            return ref.tool ? `mcp:${ref.server}#${ref.tool}` : `mcp:${ref.server}`;
        case "agent":
            return `agent:${ref.agent}`;
        case "team":
            // 团的身份是「哪个插件声明的哪一支团」——`experts[].id` 只在**插件内**唯一
            // （parseExperts 的 seenIds 是逐插件的），所以门牌号必须带插件名；只写 id 时
            // 两个插件各有一支 `ops-team` 会指向同一张卡。
            return `team:${ref.plugin}:${ref.team}`;
        case "codex":
            return CODEX_CAPABILITY_REF;
    }
}
/** Runtime guard for IPC/JSON boundaries. Unknown payloads must degrade to
 * undefined instead of reaching renderer code as an unchecked assertion. */
export function parseConnectorAuthorizeResult(value) {
    if (!value || typeof value !== "object" || Array.isArray(value))
        return undefined;
    const raw = value;
    if (raw.ok === true) {
        const account = raw.account;
        if (account !== undefined) {
            if (!account || typeof account !== "object" || Array.isArray(account))
                return undefined;
            const candidate = account;
            if (typeof candidate.label !== "string" || candidate.detail !== undefined && typeof candidate.detail !== "string")
                return undefined;
            return { ok: true, account: { label: candidate.label, ...(typeof candidate.detail === "string" ? { detail: candidate.detail } : {}) } };
        }
        return { ok: true };
    }
    if (raw.ok !== false)
        return undefined;
    const failure = raw.failure;
    if (!failure || typeof failure !== "object" || Array.isArray(failure))
        return undefined;
    const f = failure;
    const codes = [
        "bundle_syncing", "bundle_missing", "bundle_corrupt", "cli_missing", "runtime_missing",
        "browser_open_failed", "approval_timeout", "approval_declined", "identity_mismatch",
        "binding_persist_failed", "probe_failed", "config_unavailable", "config_corrupt", "needs_app_config",
    ];
    const code = typeof f.code === "string" ? codes.find((candidate) => candidate === f.code) : undefined;
    if (!code)
        return undefined;
    if (f.detail !== undefined && typeof f.detail !== "string")
        return undefined;
    const steps = ["preflight", "config", "auth_link", "browser", "approval", "identity", "binding", "finalize"];
    const step = f.step === undefined ? undefined : typeof f.step === "string" ? steps.find((candidate) => candidate === f.step) : undefined;
    if (f.step !== undefined && !step)
        return undefined;
    if (f.retryable !== undefined && typeof f.retryable !== "boolean")
        return undefined;
    const blocked = raw.blocked === undefined
        ? undefined
        : raw.blocked === "no-config" || raw.blocked === "corrupted-config" || raw.blocked === "needs-app-config"
            ? raw.blocked
            : undefined;
    if (raw.blocked !== undefined && blocked === undefined)
        return undefined;
    const common = {
        code,
        ...(typeof f.detail === "string" ? { detail: f.detail } : {}),
        ...(step ? { step } : {}),
        ...(typeof f.retryable === "boolean" ? { retryable: f.retryable } : {}),
    };
    if (code === "browser_open_failed") {
        if (typeof f.verificationUrl !== "string" || !f.verificationUrl || f.manualOpen !== true)
            return undefined;
        return {
            ok: false,
            ...(blocked ? { blocked } : {}),
            failure: { ...common, code, verificationUrl: f.verificationUrl, manualOpen: true },
            ...(typeof raw.error === "string" ? { error: raw.error } : {}),
        };
    }
    if (f.verificationUrl !== undefined || f.manualOpen !== undefined)
        return undefined;
    return {
        ok: false,
        ...(blocked ? { blocked } : {}),
        failure: { ...common, code },
        ...(typeof raw.error === "string" ? { error: raw.error } : {}),
    };
}
export const TOOL_PRESETS = [
    {
        id: "readonly",
        label: "Read-only",
        description: "Only read/search tools, no file mutations or shell",
        allowedTools: ["Read", "Glob", "Grep", "WebFetch", "WebSearch"],
    },
    {
        id: "no-shell",
        label: "No Shell",
        description: "All tools except Bash",
        disallowedTools: ["Bash"],
    },
    {
        id: "full",
        label: "Full Access",
        description: "All tools available",
    },
];
/**
 * 剥图原因（单一真相源，core 事件 / renderer atom / 横幅文案共用）：
 * - `vision`：盲脑全剥，且本会话有识图工具（analyze_media）可转文字；
 * - `visionNoTool`：盲脑全剥，但本会话**没有**识图工具——只能换模型（B-46，pi 腿）；
 * - `budget`：上下文超预算逐剥（model-router，Claude 腿）。
 */
export const IMAGES_STRIPPED_REASONS = ["vision", "visionNoTool", "budget"];
/** 反序列化 guard：跨进程/跨后端到达的 reason 先过这里，不用 `as` 代替校验。 */
export function isImagesStrippedReason(value) {
    return typeof value === "string" && IMAGES_STRIPPED_REASONS.includes(value);
}
/** 反序列化 guard：limit/running 必须是正的有限数字、rejected 必须是布尔。 */
export function isSubagentLimitNoticeEvent(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    const positive = (n) => typeof n === "number" && Number.isFinite(n) && n > 0;
    return typeof v.chatId === "string" && v.chatId.length > 0
        && positive(v.limit) && positive(v.running) && typeof v.rejected === "boolean";
}
/** 输出尾巴上限：够看清一次登录/向导的收尾，又不至于把 tool_result 撑爆上下文。 */
export const COMMAND_EXEC_OUTPUT_TAIL_BYTES = 4096;
/**
 * trailer `outcome` 的合法取值——**唯一真相源**（2026-07-18 收敛）。
 *
 * 曾经是 core 的 tool-result-parser 与 ui 的 interactive-command-view 各写一份
 * `new Set([...])`，加一个终态要改两处、漏一处就静默降级成 mcp 渲染。现在两端都从
 * 这里消费，`InteractiveCommandStructured["outcome"]` 也由它派生，禁再手写联合类型。
 */
export const INTERACTIVE_COMMAND_OUTCOMES = [
    "success",
    "nonzero",
    "cancelled",
    "failed",
    "aborted",
];
/** trailer outcome 的运行时 guard（红线 #9：trailer 来自持久化文本，不可信输入面）。 */
export function isInteractiveCommandOutcome(v) {
    return typeof v === "string" && INTERACTIVE_COMMAND_OUTCOMES.includes(v);
}
/**
 * trailer 哨兵前缀。格式：`<正文>\n<!--dosia:interactive_command:<JSON>-->`。
 * 解析端（tool-result-parser.ts）定位取 `lastIndexOf`（防正文里出现假 marker 劫持
 * 解析——命令回显 / 模型转述都可能恰好包含近似字样），中间 JSON.parse 后逐字段
 * `typeof` / 枚举 guard（红线 #9），trailer 缺失或畸形 → 落回通用 mcp 渲染，不崩。
 */
export const INTERACTIVE_COMMAND_RESULT_MARKER = "dosia:interactive_command:";
/**
 * 反序列化边界防御（红线 #9）。事件跨进程送来、卡片从持久化 toolInput 复原，
 * 两处都是不可信输入面：模型可能把 command 发成数组、executionId 发成对象。
 * 消费端必须先过这道 guard，再索引字段。
 */
export function isCommandExecutionRequest(v) {
    if (!v || typeof v !== "object")
        return false;
    const r = v;
    return (typeof r.chatId === "string" &&
        typeof r.executionId === "string" &&
        r.executionId.length > 0 &&
        typeof r.command === "string" &&
        r.command.length > 0 &&
        typeof r.explanation === "string" &&
        typeof r.dangerous === "boolean" &&
        typeof r.autoRun === "boolean" &&
        (r.cwd === undefined || typeof r.cwd === "string"));
}
// === 中断调用计量 reconciliation (REQ-41, 2026-08-19) ===
/** 一个 attempt 以「没有 native result」的方式终结的原因。取值与
 *  SessionManager._emitSyntheticTurnEnd 的 terminalReason 同一套（不新增档位）。 */
export const USAGE_RECONCILIATION_REASONS = [
    "user_interrupted",
    "subprocess_lost",
    "rebuild",
    /** SessionManager.stop() —— 它自己 emit `{cancelled:true}` 而不走
     *  _emitSyntheticTurnEnd，是第二条「无 native result」终结路径。 */
    "session_stopped",
];
export const DEFAULT_SETTINGS = {
    theme: "system",
    permissionMode: "acceptEdits",
};
/**
 * Attempts allowed per firing point (1 first fire + 3 backoffs).
 * Single source for the server runner and employee-facing UI.
 * MUST stay equal to `RETRY_BACKOFF_MS.length + 1` in
 * `apps/web/server/enterprise/task-runner.ts`.
 */
export const ENTERPRISE_TASK_MAX_ATTEMPTS = 4;
/** Runtime typeguard for `EnterpriseTaskStatus`. Single source of truth so
 *  WS event parser and HTTP body validators don't drift apart. */
export function isEnterpriseTaskStatus(value) {
    return value === "active" || value === "paused" || value === "archived";
}
/** 云端任务结果送达的闭集（见 {@link EnterpriseTask.resultDelivery}）。 */
export const TASK_RESULT_DELIVERY_VALUES = ["monitor-weekly-report"];
/** 手写守卫（反序列化边界）。加成员不会产生 TS 错误，漏改这里 = 新值在创建面被 400。 */
export function isTaskResultDelivery(value) {
    return value === "monitor-weekly-report";
}
/** Runtime typeguard for `EnterpriseTaskExecutionStatus`. */
export function isEnterpriseTaskExecutionStatus(value) {
    return (value === "queued" ||
        value === "running" ||
        value === "success" ||
        value === "failed" ||
        value === "overdue" ||
        value === "skipped");
}
/** Runtime typeguard for `EnterpriseTaskAlertKind`（反序列化边界防御，红线 #9）。
 *
 * ⚠️ 这是手写的 `===` 或链，**加联合成员不会产生任何 TS 错误**。漏改这里的后果是静默的：
 * 客户端 `parseEnterpriseTaskAlertEvent`（apps/desktop/src/main/enterprise-event-client.ts）
 * 会把整条事件判成 `null`，新告警在员工机上**完全消失且不打任何日志**。每加一个成员必须
 * 同时在 `enterprise-event-client.test.ts` 的解析格里补一条（那里有拒绝未知值的对照）。 */
export function isEnterpriseTaskAlertKind(value) {
    return (value === "run_failed" ||
        value === "auto_paused" ||
        value === "round_digest" ||
        value === "idle_paused" ||
        value === "idle_reminded");
}
/** 反序列化边界 guard：存储层读回运行状态时用它，不用 `as`。 */
export function isCronRunStatus(value) {
    return value === "running" || isCronJobLastStatus(value);
}
/** 反序列化边界 guard：存储层读回 `lastStatus` 时用它。 */
export function isCronJobLastStatus(value) {
    return value === "success" || value === "error" || value === "skipped" || value === "needs-confirm";
}
/** IPC 反序列化边界 guard（renderer 收 `cron:run-now` 回执时用，不用 `as`）。 */
export function isCronRunNowResult(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const v = value;
    return ((v.status === "started" || v.status === "alreadyRunning") &&
        typeof v.runId === "string" &&
        typeof v.startedAtMs === "number" &&
        Number.isFinite(v.startedAtMs));
}
/** {@link BugTriageStatus} 的运行时 guard —— 服务端反序列化边界用（红线 #7：解析
 *  接口输入先 guard，不用 `as` 代替校验）。生产者/消费者共用这一份判据。 */
export function isBugTriageStatus(value) {
    return value === "completed" || value === "not_run" || value === "unparsed";
}
/**
 * 归因链失败时写进飞书「归因」列的开头前缀 —— **唯一定义处**（红线 #9）。
 *
 * ⚠️ 改这里必须同步 `.claude/skills/feishu-bug-triage/SKILL.md` §Phase 2：分诊者
 * 就是靠这两个前缀识别「这一格没有结论、要以用户描述为准」。改了串却没改 skill，
 * 等于把本次修复的那个坑原样挖回来——分诊者又会把提报侧自身的状态读成 bug 根因。
 * （这条配对目前没有机械门守，靠这行注释 + admin-portal 侧的断言测试。）
 */
export const BUG_TRIAGE_CELL_PREFIX = {
    not_run: "⚠️ 本地归因未运行",
    unparsed: "⚠️ 本地归因未得出结论",
};
/**
 * {@link BugReportRecentItem}[] 的反序列化 guard（红线：解析接口输入先 guard，不用 `as`
 * 代替校验）。非数组 → []；逐条只取白名单四项，recordId 非空串才保留，其余字段类型
 * 不对就按空/缺省处理。apps/web 转发侧与 desktop 消费侧共用这一份判据。
 */
export function parseBugReportRecentItems(value) {
    if (!Array.isArray(value))
        return [];
    const out = [];
    for (const raw of value) {
        if (!raw || typeof raw !== "object")
            continue;
        const r = raw;
        if (typeof r.recordId !== "string" || r.recordId.length === 0)
            continue;
        out.push({
            recordId: r.recordId,
            userDescription: typeof r.userDescription === "string" ? r.userDescription : "",
            status: typeof r.status === "string" ? r.status : "",
            ...(typeof r.createdTimeMs === "number" && Number.isFinite(r.createdTimeMs)
                ? { createdTimeMs: r.createdTimeMs }
                : {}),
        });
    }
    return out;
}
/** Map a raw Feishu 状态字 value → the coarse {@link BugStatusBucket} the UI
 *  renders. SINGLE SOURCE OF TRUTH for this mapping (架构红线 §9). Unknown /
 *  未识别 statuses fall back to `received` (safest "we've got it" default). */
export function mapBugStatusToBucket(rawStatus) {
    switch (rawStatus) {
        case "待处理":
            return "received";
        case "已确认":
        case "修复中":
            return "in_progress";
        case "已修复待验证":
        case "已修复":
            return "fixed";
        case "已关闭":
            return "closed";
        default:
            return "received";
    }
}
/** Runtime guard for the Stop answer crossing IPC (红线 §9: no `as`). */
export function isStopAgentResult(value) {
    return (value !== null
        && typeof value === "object"
        && typeof value.nothingRunning === "boolean");
}
//# sourceMappingURL=index.js.map